import { useEffect, useRef } from "react";
import { Page } from "react-pdf";

import { useDeviceWidth } from "@vega-core/hooks/useDeviceWidth";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { isTruthy } from "@vega-core/utils/isTruthy";

import { PDFPageErrorBoundary } from "./PDFPageErrorBoundary";

const miniMapStyle = {
  active: "border-text/interactive",
  hover: "hover:border-text/interactive border-transparent shadow-card",
  container: "cursor-pointer border-4 rounded-md defaultTransition",
  overlay:
    "absolute h-full w-full top-0 left-0 bg-background/brand/opacity z-40",
  selector:
    "absolute bg-background/primary rounded-lg border-4 border-text/interactive z-50",
};

type PDFViewerMiniMapProps = {
  currentDocumentPage: number;
  setCurrentDocumentPage: (page: number) => void;
  isLoading: boolean;
  isPageMapVisible: boolean;
  totalDocumentPages: number;
  windowDimensions: { width: number; height: number };
  isDownloading: boolean;
  previewDocumentId: string;
  scrollToPage: (pageNumber: number) => void;
};

export const PDFViewerMiniMap = ({
  currentDocumentPage,
  setCurrentDocumentPage,
  isLoading,
  isPageMapVisible,
  totalDocumentPages,
  windowDimensions,
  isDownloading,
  previewDocumentId,
  scrollToPage,
}: PDFViewerMiniMapProps) => {
  const isMobile = !useDeviceWidth("md");
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const isVertical = !isMobile;

  const isMinimapAvailable =
    !isLoading &&
    isPageMapVisible &&
    totalDocumentPages > 1 &&
    isTruthy(windowDimensions) &&
    !isDownloading;

  useEffect(() => {
    if (!isMinimapAvailable || !scrollRef.current) return;
    const container = scrollRef.current;
    const activeThumb = container.querySelector(
      `[data-minimap-page="${currentDocumentPage}"]`,
    ) as HTMLElement | null;

    if (!activeThumb) return;

    if (isVertical) {
      const targetTop =
        activeThumb.offsetTop -
        container.clientHeight / 2 +
        activeThumb.clientHeight / 2;
      container.scrollTo({ top: targetTop, behavior: "smooth" });
    } else {
      const targetLeft =
        activeThumb.offsetLeft -
        container.clientWidth / 2 +
        activeThumb.clientWidth / 2;
      container.scrollTo({ left: targetLeft, behavior: "smooth" });
    }
  }, [currentDocumentPage, isMinimapAvailable, isVertical, previewDocumentId]);

  if (!isMinimapAvailable || totalDocumentPages === 0) return null;

  const handlePageChange = (pageNumber: number) => {
    setCurrentDocumentPage(pageNumber);
    scrollToPage(pageNumber);
  };

  return (
    <div
      className={cnTwMerge(
        "p-2 shrink-0",
        isVertical ? "hidden md:block" : "block md:hidden",
      )}
    >
      <div
        className={cnTwMerge(
          "flex items-center gap-2 pb-2",
          isVertical
            ? "flex-col overflow-x-visible overflow-y-auto pt-2"
            : "flex-row overflow-x-auto overflow-y-hidden",
        )}
        ref={scrollRef}
        style={{ height: isVertical ? windowDimensions.height - 185 : 140 }}
      >
        {Array.from({ length: totalDocumentPages }, (_, index) => {
          const pageNumber = index + 1;
          const isActive = currentDocumentPage === pageNumber;

          return (
            <div
              className={cnTwMerge(
                miniMapStyle.container,
                isActive ? miniMapStyle.active : miniMapStyle.hover,
                "shrink-0 relative",
              )}
              data-active={isActive || undefined}
              data-minimap-page={pageNumber}
              key={pageNumber}
              onClick={() => handlePageChange(pageNumber)}
            >
              <div style={{ width: 72, height: 93, overflow: "hidden" }}>
                <PDFPageErrorBoundary
                  fallback={<div className="skeleton-box h-28 w-[72px]" />}
                >
                  <Page
                    className="rounded border-transparent"
                    loading={<div className="skeleton-box h-28 w-[72px]" />}
                    pageNumber={pageNumber}
                    renderAnnotationLayer={false}
                    renderTextLayer={false}
                    width={72}
                  />
                </PDFPageErrorBoundary>
              </div>
              {isActive ? (
                <>
                  <div className={miniMapStyle.overlay} />
                  <div
                    className={cnTwMerge(
                      miniMapStyle.selector,
                      isVertical
                        ? "h-3 w-9 -top-2 left-1/2 -translate-x-1/2"
                        : "h-9 w-3 -left-2 top-1/2 -translate-y-1/2",
                    )}
                  />
                  <div
                    className={cnTwMerge(
                      miniMapStyle.selector,
                      isVertical
                        ? "h-3 w-9 -bottom-2 left-1/2 -translate-x-1/2"
                        : "h-9 w-3 -right-2 top-1/2 -translate-y-1/2",
                    )}
                  />
                </>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};
